import axios from 'axios';
import authService from './authService';
import { getCurrentBorerConfig } from '../config/borerEnvironmentConfig';

class BorerApiService {
  constructor() {
    this.endpoint = '/borer-detector-request';
  }

  get baseUrl() {
    return `${getCurrentBorerConfig().apiBaseUrl}/api/indirectmonitoring`;
  }

  async submitDetectorRequest(requestData) {
    try {
      const accessToken = await authService.getAccessToken();

      this.validateRequest(requestData);

      const response = await axios.post(
        `${this.baseUrl}${this.endpoint}`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          timeout: 60000,
        }
      );

      return {
        success: true,
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      console.error('API request failed:', error);

      let errorMessage = 'Unknown error occurred';
      let statusCode = null;
      let responseData = null;

      if (error.response) {
        statusCode = error.response.status;
        responseData = error.response.data;

        if (statusCode === 401) {
          authService.clearToken();
          errorMessage = 'Authentication failed. Please try again.';
        } else if (statusCode === 400) {
          errorMessage = `Bad Request: ${responseData?.message || 'Invalid request data'}`;
        } else if (statusCode === 403) {
          errorMessage = 'Access forbidden. Check your permissions.';
        } else if (statusCode === 404) {
          errorMessage = 'API endpoint not found.';
        } else if (statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `HTTP ${statusCode}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Check your network connection.';
      } else {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        statusCode,
        responseData,
        originalError: error,
      };
    }
  }

  validateRequest(requestData) {
    const requiredFields = [
      'photo01',
      'companyId',
      'companyUnitId',
      'companyFarmId',
      'trickSectionFieldId',
      'collectDateTime',
    ];

    const missingFields = requiredFields.filter(
      field => requestData[field] === undefined || requestData[field] === null || requestData[field] === ''
    );

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    const numericFields = ['companyId', 'companyUnitId', 'companyFarmId', 'trickSectionFieldId'];
    numericFields.forEach(field => {
      if (isNaN(requestData[field]) || requestData[field] <= 0) {
        throw new Error(`${field} must be a positive number`);
      }
    });

    if (requestData.latitude !== null && requestData.latitude !== '') {
      const lat = parseFloat(requestData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        throw new Error('Latitude must be a number between -90 and 90');
      }
    }

    if (requestData.longitude !== null && requestData.longitude !== '') {
      const lng = parseFloat(requestData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        throw new Error('Longitude must be a number between -180 and 180');
      }
    }

    try {
      new Date(requestData.collectDateTime).toISOString();
    } catch (e) {
      throw new Error('Invalid date format for collectDateTime');
    }

    if (!this.isValidBase64(requestData.photo01)) {
      throw new Error('photo01 is not a valid base64 image');
    }
  }

  isValidBase64(str) {
    try {
      const decoded = atob(str);
      const encoded = btoa(decoded);
      return encoded === str && str.length > 0;
    } catch (e) {
      return false;
    }
  }

  formatRequestForDisplay(requestData) {
    const displayData = { ...requestData };
    if (displayData.photo01) {
      displayData.photo01 = `${displayData.photo01.substring(0, 50)}... (${displayData.photo01.length} characters)`;
    }
    return displayData;
  }
}

export default new BorerApiService();
